import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronDown, Check, Plus, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CreateWorkspaceDialog } from '@/routes/workspaces/CreateWorkspaceDialog';

export function WorkspaceSelector() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workspaces, activeWorkspace, setActiveWorkspace } = useWorkspaceStore();
  const [open, setOpen] = useState(false);

  const handleSwitch = (ws: typeof activeWorkspace) => {
    if (!ws || ws.id === activeWorkspace?.id) {
      setOpen(false);
      return;
    }
    setActiveWorkspace(ws);
    setOpen(false);
    queryClient.invalidateQueries();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 max-w-[200px]">
          <Building2 className="h-4 w-4 shrink-0" />
          <span className="truncate text-sm">
            {activeWorkspace?.name ?? 'Select Workspace'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2" align="start">
        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1 px-2">
          Workspaces
        </p>
        <div className="space-y-0.5">
          {workspaces.map((ws) => (
            <button
              key={ws.id}
              onClick={() => handleSwitch(ws)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors text-left',
                ws.id === activeWorkspace?.id && 'bg-muted',
              )}
            >
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{ws.name}</span>
              {ws.id === activeWorkspace?.id && (
                <Check className="h-4 w-4 shrink-0 text-brand-primary" />
              )}
            </button>
          ))}
        </div>
        <Separator className="my-2" />
        <button
          onClick={() => {
            setOpen(false);
            navigate('/workspaces');
          }}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors"
        >
          <Settings className="h-4 w-4 text-muted-foreground" />
          Manage Workspaces
        </button>
        <CreateWorkspaceDialog
          trigger={
            <button className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors">
              <Plus className="h-4 w-4 text-muted-foreground" />
              Create Workspace
            </button>
          }
        />
      </PopoverContent>
    </Popover>
  );
}
